
import { useMemo } from "react";
import {
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ComposedChart,
  ResponsiveContainer,
  Bar,
  Legend,
} from "recharts";
import { subMonths, format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Transaction } from "@/types/transaction";
import { formatCurrency } from "@/lib/utils/currencyUtils";

interface CashFlowChartProps {
  transactions: Transaction[];
}

export function CashFlowChart({ transactions }: CashFlowChartProps) {
  const chartData = useMemo(() => {
    const today = new Date();

    // Gerar meses incluindo o mês atual e meses próximos (para incluir transações futuras)
    const last6Months = Array.from({ length: 6 }, (_, i) => {
      const date = subMonths(today, 3 - i); // 3 meses atrás, atual, 2 meses futuros
      return {
        month: format(date, "MMM", { locale: ptBR }),
        year: format(date, "yyyy"),
        date: date,
        income: 0,
        expenses: 0,
        balance: 0,
      };
    });

    // Agrupar transações por mês
    transactions?.forEach((transaction) => {
      // Ensure transaction.date is a Date object
      const transactionDate = transaction.date instanceof Date
        ? transaction.date
        : new Date(transaction.date);

      const monthData = last6Months.find(
        (data) =>
          format(data.date, "MM/yyyy") ===
          format(transactionDate, "MM/yyyy")
      );

      if (monthData) {
        if (transaction.type === "income" && transaction.is_paid) {
          monthData.income += Number(transaction.amount);
        } else if (transaction.type === "expense" && transaction.is_paid) {
          monthData.expenses += Number(transaction.amount);
        }
        monthData.balance = monthData.income - monthData.expenses;
      }
    });

    return last6Months;
  }, [transactions]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;

    return (
      <div className="bg-popover backdrop-blur-sm border border-border/50 rounded-xl shadow-2xl p-5 min-w-64 animate-in fade-in-0 zoom-in-95 duration-200">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-2 h-2 rounded-full bg-primary animate-pulse"></div>
          <p className="font-semibold text-foreground">{label}</p>
        </div>
        <div className="space-y-2">
          {payload.map((entry: any, index: number) => (
            <div key={index} className="flex justify-between items-center p-2 rounded-lg bg-muted/50 border border-border/30">
              <span className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full shadow-lg"
                  style={{
                    background: `linear-gradient(135deg, ${entry.color}, ${entry.color}dd)`,
                    boxShadow: `0 0 8px ${entry.color}40`
                  }}
                ></div>
                <span className="font-medium text-sm">{entry.name}</span>
              </span>
              <span className="font-bold" style={{ color: entry.color }}>
                {formatCurrency(entry.value)}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Wrapper function for YAxis tick formatter
  const yAxisTickFormatter = (value: any) => {
    return formatCurrency(value);
  };

  return (
    <div className="relative">
      <ResponsiveContainer width="100%" height={350}>
        <ComposedChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
          <defs>
            <linearGradient id="dashboardIncomeGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#10b981" stopOpacity={0.9} />
              <stop offset="100%" stopColor="#10b981" stopOpacity={0.3} />
            </linearGradient>
            <linearGradient id="dashboardExpenseGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#ef4444" stopOpacity={0.9} />
              <stop offset="100%" stopColor="#ef4444" stopOpacity={0.3} />
            </linearGradient>
            <linearGradient id="dashboardAreaGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.6} />
              <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0.1} />
            </linearGradient>
            <filter id="dashboardShadow" x="-50%" y="-50%" width="200%" height="200%">
              <feDropShadow dx="0" dy="4" stdDeviation="8" floodOpacity="0.15" />
            </filter>
            <filter id="dashboardGlow">
              <feGaussianBlur stdDeviation="3" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          <CartesianGrid
            strokeDasharray="3 3"
            stroke="var(--border)"
            strokeOpacity={0.3}
            horizontal={true}
            vertical={false}
          />

          <XAxis
            dataKey="month"
            tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11, fontWeight: 500 }}
            tickLine={false}
            axisLine={false}
            dy={10}
          />

          <YAxis
            tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11, fontWeight: 500 }}
            tickLine={false}
            axisLine={false}
            tickFormatter={yAxisTickFormatter}
            dx={-10}
          />

          <Tooltip content={<CustomTooltip />} />

          <Legend
            content={({ payload }) => {
              if (!payload || payload.length === 0) return null;

              return (
                <div className="flex justify-center items-center gap-6 mt-4 px-4">
                  {payload.map((entry: any, index: number) => (
                    <div key={index} className="flex items-center gap-2 text-sm">
                      <div
                        className="w-4 h-4 rounded shadow-sm"
                        style={{
                          background: entry.dataKey === 'income'
                            ? 'linear-gradient(135deg, #10b981, #10b98199)'
                            : entry.dataKey === 'expenses'
                              ? 'linear-gradient(135deg, #ef4444, #ef444499)'
                              : 'linear-gradient(135deg, #8b5cf6, #8b5cf699)'
                        }}
                      />
                      <span className="font-medium text-foreground/80">{entry.value}</span>
                    </div>
                  ))}
                </div>
              );
            }}
            wrapperStyle={{ paddingTop: '10px' }}
          />

          <Bar
            dataKey="income"
            fill="url(#dashboardIncomeGradient)"
            name="Receitas"
            radius={[4, 4, 0, 0]}
            style={{ filter: 'url(#dashboardShadow)' }}
          />
          <Bar
            dataKey="expenses"
            fill="url(#dashboardExpenseGradient)"
            name="Despesas"
            radius={[4, 4, 0, 0]}
            style={{ filter: 'url(#dashboardShadow)' }}
          />
          <Area
            type="monotone"
            dataKey="balance"
            stroke="#8b5cf6"
            strokeWidth={3}
            fill="url(#dashboardAreaGradient)"
            name="Saldo"
            style={{ filter: 'url(#dashboardGlow)' }}
          />
        </ComposedChart>
      </ResponsiveContainer>

      {/* Modern overlay gradient */}
      <div className="absolute inset-0 bg-gradient-to-t from-background/5 via-transparent to-background/5 pointer-events-none rounded-lg"></div>
    </div>
  );
}
